import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { func, number, object } from 'prop-types';

import { connect } from 'react-redux';

import { get, has, isEmpty } from 'lodash';

import template from 'url-template';

import awsHeader from '../../helpers/awsAuth';
import { createProfileObject } from '../../actions/profileObjectActions';
import { fetchBasAgent } from '../../actions/basAgentActions';
import { getDataError } from '../../helpers/errors';
import { enqueueSnackbar, handleNetworkError } from '../../actions/applicationMessageActions';
import { setProgressBarLoading } from '../../actions/coreActions';
import { patchItem } from '../../helpers/collections';
import { updateInventory } from '../../actions/inventoryActions';
import * as DashboardActions from '../../actions/dashboardActions';
import useInventory from '../../hooks/useInventory';
import useSelectedAgent from '../../hooks/useSelectedAgent';
import k2aBaseApi from '../../apis/key2act';
import EsmsPage from '../../components/EsmsPage';
import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import EditProfileForm from '../../components/EditProfileForm';
import InventoryTable from '../../components/InventoryTable';
import { createBulkProfileObject } from '../../actions/iotHubActions';

import PendingActionsModal from '../Modals/PendingActionsModal';
import ProfileObjectModificationModal from '../Modals/ProfileObjectModificationModal';
import ProfileObjectModificationResultModal from '../Modals/ProfileObjectModificationResultModal';

const AgentUrl = template.parse('/iothub/basagents/manage{?company,basAgent}');
const AgentsUrl = template.parse('/iothub/basagents{?company}');
const InventoryUrl= template.parse('/iothub/basagents/manage/inventory/{driverType}{?basAgent,company}');

const CompanyProfilesUrl = template.parse('/companies/{companyId}/basprofiles');

const InitialProfile = Object.freeze({
    basType:   null,
    deviceIds: [],
    name:      ''
});

const Types = {
    boolean: 'Boolean',
    float:   'Float',
    integer: 'Integer',
    text:    'Text'
};

const createProfile = async (companyId, profile) => {
    const header = await awsHeader();
    const response = await k2aBaseApi.post(
        CompanyProfilesUrl.expand({ companyId }),
        profile,
        header
    );
    return {
        ...profile,
        ...response.data
    };
};

// TODO: Profile creation error was not surfaced (probably because the error key does not exist in the form)

const NiagaraProfileCreatePage = ({
    companyInfo,
    createBulkProfileObject,
    createProfile,
    createProfileObject,
    enqueueSnackbar,
    handleNetworkError,
    history,
    location,
    selectedOBIXPath,
    setProgressBarLoading,
    setWorkBookMode,
    treeCacheId,
    updateInventory,
    workbookModeEnabled
}) => {
    const [dataErrors, setDataErrors] = useState({});
    const [requestErrors, setRequestErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [completedObjects, setCompletedObjects] = useState(0);
    const [pendingObjects, setPendingObjects] = useState(0);
    const [pendingActions, setPendingActions] = useState({});
    const [showPendingActionsModal, setShowPendingActionsModal] = useState(false);

    const [newProfile, setNewProfile] = useState({ ...InitialProfile });
    const values = new URLSearchParams(location.search);
    const { basType, loading: loadingSelectedAgent, selectedAgent } =
        useSelectedAgent(values.get('basAgent'));
    const { loading: loadingInventory, inventory } = useInventory();
    const hasErrors = requestErrors.length > 0;
    const isLoading = loadingSelectedAgent || loadingInventory;

    useEffect(() => {
        setNewProfile((profile) => ({
            ...profile,
            basType
        }));
    }, [basType]);

    const preparePendingActions = deviceIds => {
        const pendingActionData = {
            actionableData: {
                title: 'To be created',
                data:  []
            },
            nonActionableData: {
                title: 'Will not be created',
                data:  []
            }
        };

        deviceIds.forEach(deviceId => {
            const device = inventory[deviceId];

            if (device.dataType && has(Types, device.dataType)) {
                pendingActionData.actionableData.data.push(device);
            } else if (!device.isLeaf) {
                const ignoredDevice = {
                    ...device,
                    reason: 'Not trend-able.',
                    action: 'No action necessary.'
                };

                pendingActionData.nonActionableData.data.push(ignoredDevice);
            } else if (!device.dataType || !has(Types, device.dataType)) {
                const ignoredDevice = {
                    ...device,
                    reason: 'Data type not selected.',
                    action: 'Select a data type.'
                };

                pendingActionData.nonActionableData.data.push(ignoredDevice);
            }
        });

        setPendingActions(pendingActionData);
    };

    const handleCancel = () => {
        setNewProfile({...InitialProfile });
    };

    const handleCellChange = (name, value, row) => {
        updateInventory(row.oBIXPath, {
            ...row,
            [name]: value
        });
    };

    const handleChange = modifiedProfile => {
        setNewProfile(modifiedProfile);
    };

    const handleDeviceSelection = deviceIds => {
        setNewProfile({
            ...newProfile,
            deviceIds
        });
    };

    const handleResultClose = () => {
        setRequestErrors([]);
    };

    const handleCreate = () => {
        const { deviceIds } = newProfile;

        preparePendingActions(deviceIds);

        setShowPendingActionsModal(true);
    };

    const handleSubmit = async () => {
        setRequestErrors([]);
        setDataErrors({});

        let { deviceIds, ...profile } = newProfile;
        setProgressBarLoading(true);
        if (!profile.id) {
            try {
                profile = await createProfile(
                    companyInfo.companyId,
                    profile
                );
                setNewProfile({
                    ...newProfile,
                    ...profile
                });
            } catch (error) {
                const dataErrors = getDataError(error);
                if (!dataErrors) {
                    handleNetworkError(error);
                    return;
                }
                setDataErrors(dataErrors);
                return;
            }
        }


        updateInventory(selectedOBIXPath, {
            profileId:   profile.id,
            profileName: profile.name
        });

        const actionableDevices = get(pendingActions, 'actionableData.data', []);
        const profileObjects = [];
        actionableDevices.forEach(item => {
            item = _getParams(item, selectedOBIXPath);
            profileObjects.push({
                'oBIXPath':       item.oBIXPath,
                'basProfileId':   profile.id,
                'objectType':     item.type,
                'name':           item.name,
                'dataType':       item.dataType,
                'sampleInterval': item.sampleInterval || '15',
                'changeOfValue':  item.sampleInterval === 0 || false
            });
        });

        if (!isEmpty(profileObjects)) {
            setIsSubmitting(true);
            await createBulkProfileObject(profile.id, profileObjects, redirectToInventory, setIsSubmitting, setRequestErrors, setCompletedObjects, setPendingObjects);
            setProgressBarLoading(false);
        } else {
            enqueueSnackbar(`Profile created successfully`, 'success');
            setProgressBarLoading(false);
            redirectToInventory();
        }
    };

    const _getParams = (profileObject, parentOBIXPath) => {
        if (!profileObject.oBIXPath) return profileObject;

        profileObject.oBIXPath = _calculateRelativePath(profileObject, parentOBIXPath);

        if (!has(profileObject, 'objectType') && has(profileObject, 'type')) profileObject.objectType = profileObject.type;

        return profileObject;
    };

    const _calculateRelativePath = ({ oBIXPath }, parentPath) => {
        return oBIXPath.replace(parentPath, '');
    };

    const redirectToAgents = () => {
        history.push(AgentsUrl.expand({
            company: companyInfo.companyId
        }));
    };

    const redirectToAgent = () => {
        history.push(AgentUrl.expand({
            company:  companyInfo.companyId,
            basAgent: selectedAgent.id
        }));
    };

    const redirectToInventory = () => {
        const driverType =
            selectedAgent.driverConfiguration[0].basType.toLowerCase();
        history.push(InventoryUrl.expand({
            company:  companyInfo.companyId,
            basAgent: selectedAgent.id,
            driverType
        }));
    };

    const redirectToIotHub = () => {
        history.push('/iothub' + location.search);
    };

    const toggleWorkbookMode = () => {
        setWorkBookMode(!workbookModeEnabled);
    };

    if (!values.has('selectedOBIXPath')) {
        redirectToInventory();
        return null;
    }

    if (!selectedAgent) {
        return null;
    }

    return (
        <EsmsPage
            child="Create New Profile"
            handlers={[
                redirectToIotHub,
                redirectToAgents,
                redirectToAgent
            ]}
            parents={[
                companyInfo.companyName,
                'BAS Agents',
                selectedAgent.name,
                'Profiles'
            ]}
            sidebar={(
                <EditProfileForm
                    errors={dataErrors}
                    onCancel={handleCancel}
                    onChange={handleChange}
                    onSubmit={handleCreate}
                    profile={newProfile}
                />
            )}
            title="Select Objects"
        >
            <ActionBar>
                <ActionBarItem
                    onClick={toggleWorkbookMode}
                    type="edit"
                >
                    Workbook Mode
                </ActionBarItem>
            </ActionBar>
            <InventoryTable
                editable
                hiddenColumns={['action', 'profileName']}
                inventory={inventory}
                loading={isLoading}
                onCellChange={handleCellChange}
                onDeviceSelection={handleDeviceSelection}
                rootId={selectedOBIXPath}
                treeCacheId={treeCacheId}
                type="niagara"
            />
            <ProfileObjectModificationModal
                completed={completedObjects}
                modificationType="create"
                open={isSubmitting}
                pending={pendingObjects}
            />
            <ProfileObjectModificationResultModal
                errors={requestErrors}
                isCreate={true}
                modificationType="create"
                onClose={handleResultClose}
                open={hasErrors}
            />
            <PendingActionsModal
                onClose={() => setShowPendingActionsModal(false)}
                onConfirm={handleSubmit}
                open={showPendingActionsModal}
                actionData={pendingActions}
                title="Pending Profile Object Actions"
            />
        </EsmsPage>
    );
};

NiagaraProfileCreatePage.defaultProps = {
    createProfile,
    fetchBasAgent:      () => {},
    handleNetworkError: () => {},
    patchItem,
    updateInventory:    () => {}
};

NiagaraProfileCreatePage.propTypes = {
    companyInfo:        object,
    createProfile:      func,
    fetchBasAgent:      func,
    handleNetworkError: func,
    inventory:          object,
    patchItem:          func,
    selectedAgent:      object,
    selectedInventory:  object,
    treeCacheId:        number.isRequired,
    updateInventory:    func
};

export const mapStateToProps = ({ basAgent, companyInfo, inventory, ...state }) => {
    const {
        treeCacheId,
        selectedOBIXPath
    } = inventory;
    return {
        companyInfo,
        devices:             state.device.devices,
        selectedOBIXPath,
        treeCacheId,
        workbookModeEnabled: state.dashboard.workbookModeEnabled
    };
};

const mapDispatchToProps = {
    createBulkProfileObject,
    createProfileObject,
    enqueueSnackbar,
    fetchBasAgent,
    handleNetworkError,
    setProgressBarLoading,
    updateInventory,
    ...DashboardActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NiagaraProfileCreatePage));
